import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import AttemptFilesModal from 'components/@common/Modals/AttemptFilesModal';
import cx from 'clsx';
import { Box, Button, Chip, Container, IconButton, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { resetAssignmentDetail } from 'state/modules/assignmentTracking/thunks';
import { getAssignment } from 'state/modules/entity/thunks/assignment';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { getClassroom } from 'state/modules/entity/selectors/classroom-selectors';
import { translate } from 'translations/translator';
import { AppStoreState } from 'types';
import { MESSAGE_SENDER as MessageSender } from 'types/enums';
import AssignmentDetails from './AssignmentDetails';
import AttemptStatusChip from './AttemptStatusChip';
import AttemptStatusDropdown from './AttemptStatusDropdown';
import { SATableHeader } from './SATableHeader';
import { SATableLayout } from './SATableLayout';
import { formatUiDate } from 'util/date-util';
import A from 'translations/variants/assignmentTracker';
import { TableColumnProps } from '@lwtears/lwt-common-frontend/lib/Table';

interface StudentAttempt {
  student: any;
  attempt: any;
}

const getTranslation = (key: A, options?) => translate('assignmentTracker', key, options);

export const Assignment: React.FC = () => {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  const [, { openModal }] = useModal();
  const { assignmentId: assignmentIdParam } = useParams();
  const [attemptStatusFilter, setAttemptStatusFilter] = useState(null);
  const assignmentId = Number(assignmentIdParam);

  const assignment = useSelector(({ entity }: AppStoreState) => entity.assignmentDetail);
  const classroom = useSelector(state =>
    getClassroom(state, { classroomId: assignment?.classroomId })
  );
  const classrooms = useSelector(({ entity }: AppStoreState) => entity.classrooms) ?? [];
  const studentsById = useSelector(({ entity }: AppStoreState) => entity.studentsById) ?? {};

  useEffect(() => {
    dispatch(getAssignment(assignmentId, { includeAttempts: true }));
    if (classrooms.length === 0) dispatch(fetchUserClassrooms());
    return () => {
      dispatch(resetAssignmentDetail());
    };
  }, [dispatch, assignmentId, classrooms]);

  const studentAttempts = useMemo<StudentAttempt[]>(
    () =>
      assignment?.attempts?.reduce((acc, attempt) => {
        const student = studentsById[attempt.studentId];
        if (attemptStatusFilter === 'all') return [...acc, { student, attempt }];
        if (!student || (attemptStatusFilter && attemptStatusFilter !== attempt.status)) return acc;
        return [...acc, { student, attempt }];
      }, []) ?? [],
    [assignment, studentsById, attemptStatusFilter]
  );

  const handleAttemptStatusFilterChange = (_, value) => {
    setAttemptStatusFilter(value);
  };

  const handleFilesClick = e => {
    const studentAttempt = studentAttempts.find(
      studentAttempt => studentAttempt.student.id === Number(e.currentTarget.value)
    );

    if (!studentAttempt) return;

    openModal({
      titleText: `${getTranslation(A.attemptFiles)}: ${studentAttempt.student.firstName} ${
        studentAttempt.student.lastName
      }`,
      body: <AttemptFilesModal files={studentAttempt.attempt.files} />
    });
  };

  const handlePreviousClick = () => navigate(-1);

  if (!assignment) return null;

  const assignedToText = assignment.studentIds
    ? getTranslation(A.assignedStudents, {
        assignedCount: assignment.studentIds.length,
        classroomName: classroom?.name
      })
    : classroom?.name;

  const getMessages = ({ attempt }: StudentAttempt) => {
    const { latestMessage } = attempt;

    if (!latestMessage) {
      return <Typography variant="body1">{getTranslation(A.noMessages)}</Typography>;
    }

    const isNew = !latestMessage.dateOpened && latestMessage.sender === MessageSender.STUDENT;

    let messageText = latestMessage.text;
    if (latestMessage.file) {
      // TODO: Different message filetypes here?
      messageText = getTranslation(A.audioMessage);
    }

    if (latestMessage.sender === MessageSender.EDUCATOR) {
      messageText = `${getTranslation(A.educatorMessage)} ${messageText}`;
    }

    return (
      <Typography variant="body1" className={classes.newMessageText}>
        {isNew && (
          <Chip
            label={getTranslation(A.newMessage)}
            size="small"
            classes={{
              label: classes.newMessageChipLabel,
              root: classes.newMessageChip
            }}
          />
        )}
        <span className={cx(classes.messageText, { [classes.newMessageText]: isNew })}>
          {messageText}
        </span>
      </Typography>
    );
  };

  const getAssignmentStatus = ({ attempt }: StudentAttempt) => (
    <AttemptStatusChip status={attempt.status} />
  );

  const getAssignmentAttemptFiles = ({ student, attempt }: StudentAttempt) => {
    if (!attempt.files?.length) {
      return <Typography variant="body1">{getTranslation(A.noAttemptFiles)}</Typography>;
    }

    return (
      <Button
        value={student.id}
        onClick={handleFilesClick}
        className={classes.fileText}
        disableRipple
      >
        <Typography variant="body1">
          {getTranslation(A.files, { fileCount: attempt.files.length })}
        </Typography>
      </Button>
    );
  };

  const columns: TableColumnProps<any>[] = [
    {
      label: getTranslation(A.studentName),
      getValue({ student }): React.ReactElement {
        return (
          <RouterLink to={`students/${student.id}`} className={classes.link}>
            <Typography variant="h6" className={classes.link}>
              {student.firstName} {student.lastName}
            </Typography>
          </RouterLink>
        );
      },
      options: {
        width: '20%',
        sortable: true,
        sortKey: 'title'
      }
    },
    {
      label: getTranslation(A.messages),
      getValue: getMessages,
      options: {
        width: '35%',
        sortable: false
      }
    },
    {
      label: getTranslation(A.attemptStatus),
      getValue: getAssignmentStatus,
      options: {
        width: '15%',
        sortable: true,
        sortKey: 'attempt.status'
      }
    },
    {
      label: getTranslation(A.attemptFiles),
      getValue: getAssignmentAttemptFiles,
      options: {
        width: '27%',
        sortable: false
      }
    },
    {
      label: '',
      getValue({ student }): React.ReactElement {
        return (
          <Box display="flex" alignItems="center" fontWeight="fontWeightBold">
            <RouterLink to={`students/${student.id}`} className={classes.link}>
              <IconButton key={`icon-button-${student.id}`}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </RouterLink>
          </Box>
        );
      },
      options: {
        width: '3%',
        sortable: false
      }
    }
  ];

  return (
    <Container maxWidth="xl" disableGutters>
      <Box py={3} px={5} bgcolor="primary.light" className={classes.container}>
        <SATableHeader
          title={assignment.title}
          renderBackArrow={true}
          handlePreviousClick={() => handlePreviousClick()}
        />
        <AssignmentDetails
          fields={[
            { label: getTranslation(A.assignedTo), value: assignedToText },
            {
              label: getTranslation(A.dateAssigned),
              value: formatUiDate(new Date(assignment.dateAssigned))
            },
            {
              label: getTranslation(A.dateDue),
              value: formatUiDate(new Date(assignment.dateDue))
            }
          ]}
        >
          <Box ml="auto">
            <Typography>{getTranslation(A.attemptStatus)}</Typography>
            <AttemptStatusDropdown
              displayEmpty
              name="attemptStatusFilter"
              value={attemptStatusFilter ?? 'all'}
              onChange={handleAttemptStatusFilterChange}
            />
          </Box>
        </AssignmentDetails>
        <SATableLayout
          data={studentAttempts}
          columns={columns}
          filters={[]}
          titleNoData={getTranslation(A.titleNoData)}
          subTitleNoData={getTranslation(A.subTitleNoData)}
          buttonTitleNoData={getTranslation(A.buttonTitleNoData)}
          handleNoDataClick={() => undefined}
        />
      </Box>
    </Container>
  );
};

/*
    124px = HeightFooter + HeightHeader
*/
const footerAndHeaderHeight = 150;
const useStyles = theme =>
  makeStyles(() => ({
    container: {
      height: `calc(100vh - ${footerAndHeaderHeight}px)`,
      overflow: 'auto'
    },
    newMessageChip: () => ({
      backgroundColor: theme.palette.error.main,
      marginRight: theme.spacing(1)
    }),
    newMessageChipLabel: () => ({
      color: theme.palette.common.white,
      textTransform: 'none',
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium
    }),
    messageText: () => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: theme.typography.fontWeightMedium,
      maxWidth: 450
    }),
    newMessageText: () => ({
      fontWeight: theme.typography.fontWeightBold
    }),
    fileText: {
      textTransform: 'none !important',
      padding: 0,
      width: 'inherit',
      height: 'inherit',
      minWidth: 'inherit',
      justifyContent: 'left'
    } as any,
    link: () => ({
      color: `${theme.palette.text.primary} !important`,
      textDecoration: 'none !important',
      '&:hover': { color: 'inherit', textDecoration: 'underline !important' }
    })
  }));
